import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TeamBadge } from "./TeamBadge";

// Game Centre controls in the second (top) nav bar. Always uncollapsed.
// ONE row: [‹ Prev Game] · Game Recap · [T1] Lineup · [T2] Lineup · | Period tabs · [Next Game ›]
// Prev/Next game surfers only render when handlers are supplied (schedule
// game-centre); the standalone Game Centre omits them.
export default function GameCentreNavBar({
    view, setView,
    teamFilter, setTeamFilter,
    lineupTeams = [],
    periods = [], insPeriod, setInsPeriod,
    statTabs = [], statSel, setStatSel,
    onPrevGame, onNextGame,
}) {
    const pill = (on, color) => (on
        ? { background: `${color}26`, borderColor: `${color}59`, color: "#fff" }
        : { borderColor: "rgba(255,255,255,0.12)" });
    const Btn = ({ on, color = "#9D4CDD", onClick, testId, children }) => (
        <button type="button" onClick={onClick} data-testid={testId} style={pill(on, color)}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg border text-[10px] font-black uppercase tracking-wider transition-colors ${on ? "" : "bg-black/25 text-[#94A3B8] hover:text-white"}`}>
            {children}
        </button>
    );
    const Surf = ({ dir, onClick, testId }) => (
        <button type="button" disabled={!onClick} onClick={onClick || undefined} data-testid={testId}
            title={dir === "prev" ? "Previous game" : "Next game"}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-white/12 bg-black/25 text-[10px] font-black uppercase tracking-wider text-[#94A3B8] hover:text-white hover:border-white/25 disabled:opacity-25 disabled:cursor-not-allowed transition-colors">
            {dir === "prev"
                ? <><ChevronLeft className="h-3.5 w-3.5" /> Prev Game</>
                : <>Next Game <ChevronRight className="h-3.5 w-3.5" /></>}
        </button>
    );

    const surfEnabled = onPrevGame !== undefined || onNextGame !== undefined;

    return (
        <div className="w-full flex flex-wrap items-center justify-center gap-2 py-1" data-testid="game-centre-nav">
            {surfEnabled && <Surf dir="prev" onClick={onPrevGame} testId="pgc-prev-game" />}
            <Btn on={view === "team_summary"} onClick={() => setView("team_summary")} testId="pgc-view-team_summary">Game Recap</Btn>
            {lineupTeams.map((t) => {
                const on = view === "lineup" && teamFilter === t.id;
                return t.available ? (
                    <Btn key={t.id} on={on} color={t.color} testId={`pgc-lineup-btn-${t.id}`}
                        onClick={() => { setView("lineup"); setTeamFilter(t.id); }}>
                        <TeamBadge teamId={t.id} size="xs" asLink={false} /> Lineup
                    </Btn>
                ) : (
                    <span key={t.id} data-testid={`pgc-lineup-btn-${t.id}-disabled`} title="No lineup available for this game"
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg border border-white/[0.06] bg-black/10 text-[10px] font-black uppercase tracking-wider text-white/30 opacity-40 grayscale cursor-not-allowed">
                        <TeamBadge teamId={t.id} size="xs" asLink={false} /> Lineup
                    </span>
                );
            })}
            {periods.length > 0 && (
                <span className="inline-flex flex-wrap items-center gap-1.5 border-l border-white/10 pl-2 ml-0.5" data-testid="pgc-period-tabs">
                    <span className="text-[9px] uppercase tracking-[0.14em] text-[#475569] font-bold mr-0.5">Period</span>
                    {periods.map(({ k, label, color }) => (
                        <Btn key={k} on={insPeriod === k} color={color} onClick={() => setInsPeriod(k)} testId={`pgc-insperiod-${k}`}>
                            {insPeriod !== k && <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />}{label}
                        </Btn>
                    ))}
                </span>
            )}
            {statTabs.length > 1 && setStatSel && (
                <span className="inline-flex flex-wrap items-center gap-1.5 border-l border-white/10 pl-2 ml-0.5" data-testid="pgc-view-tabs">
                    <span className="text-[9px] uppercase tracking-[0.14em] text-[#475569] font-bold mr-0.5">Views</span>
                    {statTabs.map((t) => {
                        const on = !!(statSel && statSel[t.id]);
                        return (
                            <Btn key={t.id} on={on} color="#38BDF8" testId={`pgc-stattab-${t.id}`}
                                onClick={() => setStatSel({ [t.id]: true })}>{t.label}</Btn>
                        );
                    })}
                </span>
            )}
            {surfEnabled && <Surf dir="next" onClick={onNextGame} testId="pgc-next-game" />}
        </div>
    );
}
